import { RenderProgress } from "@/components/mist/RenderProgress";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ChevronLeft, Loader2, Music, Mic, Film, Type, Scissors, ArrowUp, ArrowDown,
  Upload, X, CheckCircle2, GripVertical, Sparkles, Download, Send,
} from "lucide-react";
import { projectsApi, pipelinesApi, streamAsset } from "@/api/mist";
import { setDirectorAsset } from "@/lib/directorPrefill";
import VaultPickButton from "@/components/mist/VaultPickButton";

function ClipThumb({ assetId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let u; let alive = true;
    if (assetId) streamAsset(assetId).then((x) => { if (alive) { u = x; setSrc(x); } }).catch(() => {});
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [assetId]);
  if (!src) return <div className="w-full h-full grid place-items-center bg-black"><Loader2 size={14} className="text-white/40 animate-spin" /></div>;
  return <video src={src} muted playsInline preload="metadata" className="w-full h-full object-cover" />;
}

function FilePick({ icon: Icon, label, accept, file, onPick, onClear, testid }) {
  const ref = useRef(null);
  const isAudio = /audio|wav|mp3|m4a|ogg/i.test(accept || "");
  const bucket = /video|mp4|mov|webm/i.test(accept || "") ? "videos" : "images";
  return (
    <div className="flex items-center gap-2 border-2 border-black bg-[#141414] p-2 shadow-[2px_2px_0px_#000]">
      <Icon size={15} className="text-[#FFD028] shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-mono text-[8px] uppercase tracking-widest text-white/45 leading-none">{label}</p>
        <p className="font-mono text-[10px] text-white truncate mt-0.5">{file ? file.name : "None selected"}</p>
      </div>
      <input ref={ref} type="file" accept={accept} hidden data-testid={testid}
        onChange={(e) => { const f = (e.target.files || [])[0]; e.target.value = ""; if (f) onPick(f); }} />
      {file ? (
        <button onClick={onClear} className="w-7 h-7 grid place-items-center border-2 border-black bg-[#141414] text-red-400"><X size={12} /></button>
      ) : (
        <>
          <button onClick={() => ref.current?.click()} data-testid={`${testid}-btn`} className="w-7 h-7 grid place-items-center border-2 border-black bg-[#FFD028] text-black"><Upload size={12} /></button>
          {!isAudio && <VaultPickButton onFile={(f) => onPick(f)} bucket={bucket} testid={`${testid}-vault`} label=""
            className="w-7 h-7 grid place-items-center border-2 border-[#00F5D4] bg-black text-[#00F5D4]" />}
        </>
      )}
    </div>
  );
}

export function StitchingStudio({ project, onBack, onCompiled }) {
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overlays, setOverlays] = useState({}); // sceneId -> text
  const [music, setMusic] = useState(null);
  const [voice, setVoice] = useState(null);
  const [outro, setOutro] = useState(null);
  const [compiling, setCompiling] = useState(false);
  const [resultId, setResultId] = useState(null);
  const [resultFilename, setResultFilename] = useState(null);
  const [outroId, setOutroId] = useState(null);      // MISTECK master outro asset
  const [useOutro, setUseOutro] = useState(false);
  const [outroBusy, setOutroBusy] = useState(false);
  const dragIdx = useRef(null);

  const load = async () => {
    try {
      const r = await projectsApi.get(project.id);
      setScenes((r.scenes || []).filter((s) => s.status === "completed"));
    } catch { toast.error("Could not load scenes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  const applyOrder = (list) => {
    // Fresh array + fresh objects with recomputed order → guarantees a re-render.
    const reindexed = list.map((s, i) => ({ ...s, sceneOrder: i }));
    setScenes(reindexed);
    Promise.all(reindexed.map((s) => projectsApi.editScene(project.id, s.id, { scene_order: s.sceneOrder }).catch(() => {})));
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= scenes.length) return;
    const list = scenes.map((s) => ({ ...s }));
    const tmp = list[idx]; list[idx] = list[j]; list[j] = tmp;
    applyOrder(list);
  };
  const onDrop = (idx) => {
    const from = dragIdx.current; dragIdx.current = null;
    if (from == null || from === idx) return;
    const list = scenes.map((s) => ({ ...s }));
    const [m] = list.splice(from, 1);
    list.splice(idx, 0, m);
    applyOrder(list);
  };

  const totalSecs = scenes.reduce((a, s) => a + (s.duration || 0), 0);

  const compile = async () => {
    if (scenes.length === 0) { toast.error("No finished scenes to join yet"); return; }
    setCompiling(true); setResultId(null);
    try {
      const r = await projectsApi.compile(project.id, {
        title: project.title, overlays, music, voiceover: voice, outro,
        outro_asset_id: useOutro ? outroId : null,
      });
      const j = await pipelinesApi.poll(r.job_id, { timeout: 480000 });
      if (j.status === "completed") {
        setResultId(j.asset_id);
        setResultFilename(j.filename || null);
        toast.success("Your reel is ready → saved to your Vault ✓");
        onCompiled?.();
      } else {
        toast.error(j.error || "Couldn't create the reel");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create the reel"); }
    finally { setCompiling(false); }
  };

  const genOutro = async () => {
    setOutroBusy(true);
    try {
      const r = await projectsApi.generateOutro();
      setOutroId(r.asset_id); setUseOutro(true);
      toast.success(r.cached ? "MISTECK outro ready (in Main Vault)" : "MISTECK outro built → saved to Main Vault ✓");
    } catch (e) { toast.error(e?.response?.data?.detail || "Outro build failed"); }
    finally { setOutroBusy(false); }
  };

  const downloadOutro = async () => {    if (!outroId) return;
    try {
      const url = await streamAsset(outroId);
      const a = document.createElement("a");
      a.href = url; a.download = "master_outro.mp4";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("Download failed"); }
  };

  // One-tap bridge: stash the freshly compiled reel and jump straight into the
  // Mist Director workspace with it pre-loaded (no download / re-upload) so the
  // user lands on the Caption stage to add captions + hashtags instantly.
  const sendToDirector = () => {
    if (!resultId) return;
    setDirectorAsset({ asset_id: resultId, filename: resultFilename, kind: "video" });
    const p = new URLSearchParams(window.location.search);
    p.set("tab", "command");
    p.set("studio", "director");
    p.delete("view"); p.delete("hub");
    window.history.pushState({ studio: "director" }, "", `${window.location.pathname}?${p}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
    toast.success("Opening Mist Director → add captions & hashtags");
  };

  return (
    <div className="h-full w-full flex flex-col overflow-hidden" data-testid="stitching-studio">
      <div className="shrink-0 flex items-center gap-2 px-4 py-2 border-b-2 border-black bg-[#141414]">
        <button onClick={onBack} data-testid="ss-back" className="w-8 h-8 grid place-items-center border-2 border-black bg-[#141414] shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><ChevronLeft size={16} /></button>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-lg text-white truncate leading-none flex items-center gap-1.5"><Scissors size={14} className="text-[#FFD028]" /> Video Joiner</p>
          <p className="font-mono text-[8px] uppercase tracking-widest text-white/40 mt-1">{scenes.length} scene{scenes.length === 1 ? "" : "s"} · {totalSecs}s timeline</p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-5">
        {/* Timeline */}
        <div>
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-white/50 mb-2">Timeline · drag or use arrows to reorder</p>
          {loading ? (
            <div className="grid place-items-center py-8"><Loader2 size={20} className="text-[#FFD028] animate-spin" /></div>
          ) : scenes.length === 0 ? (
            <p className="font-mono text-[10px] text-white/40 border-2 border-dashed border-white/15 p-4 text-center">No completed scenes yet. Render scenes in the Project Vault first.</p>
          ) : (
            <div className="space-y-2" data-testid="ss-timeline">
              {scenes.map((s, i) => (
                <div key={s.id} draggable data-testid={`ss-scene-${s.id}`}
                  onDragStart={() => { dragIdx.current = i; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(i)}
                  className="flex items-center gap-2 border-2 border-black bg-[#0d0d0d] p-2 shadow-[2px_2px_0px_#000]">
                  <GripVertical size={14} className="text-white/30 shrink-0 cursor-grab" />
                  <span className="font-mono text-[10px] font-black bg-[#FFD028] text-black w-5 h-5 grid place-items-center border border-black shrink-0">{i + 1}</span>
                  <div className="w-12 h-16 border-2 border-black bg-black overflow-hidden shrink-0"><ClipThumb assetId={s.asset_id} /></div>
                  <div className="min-w-0 flex-1">
                    <input value={overlays[s.id] || ""} onChange={(e) => setOverlays((o) => ({ ...o, [s.id]: e.target.value.slice(0, 120) }))}
                      placeholder="Subtitle / caption for this scene…" data-testid={`ss-subtitle-${s.id}`}
                      className="w-full bg-[#141414] text-white placeholder:text-white/35 border-2 border-black px-2 py-1.5 font-mono text-[10px] outline-none focus:ring-2 focus:ring-[#FFD028]" />
                    <p className="font-mono text-[8px] uppercase tracking-widest text-white/35 mt-1">{s.duration}s{s.mode === "import" ? " · import" : ""}</p>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`ss-up-${s.id}`} className="w-6 h-6 grid place-items-center border-2 border-black bg-[#141414] text-white disabled:opacity-30 active:bg-[#FFD028] active:text-black"><ArrowUp size={11} /></button>
                    <button onClick={() => move(i, 1)} disabled={i === scenes.length - 1} data-testid={`ss-down-${s.id}`} className="w-6 h-6 grid place-items-center border-2 border-black bg-[#141414] text-white disabled:opacity-30 active:bg-[#FFD028] active:text-black"><ArrowDown size={11} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Audio + assets panel */}
        <div className="space-y-2" data-testid="ss-inputs">
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-white/50">Post-production</p>
          <FilePick icon={Music} label="Background music" accept="audio/mpeg,audio/wav,.mp3,.wav" file={music} onPick={setMusic} onClear={() => setMusic(null)} testid="ss-music" />
          <FilePick icon={Mic} label="Voiceover track" accept="audio/mpeg,audio/wav,.mp3,.wav" file={voice} onPick={setVoice} onClear={() => setVoice(null)} testid="ss-voice" />
          <FilePick icon={Film} label="Outro clip (.mp4)" accept="video/mp4,video/quicktime,.mp4,.mov" file={outro} onPick={setOutro} onClear={() => setOutro(null)} testid="ss-outro" />
        </div>

        {/* MISTECK master outro — programmatic system footer */}
        <div className="border-2 border-[#FFD028] bg-[#0d0d0d] p-3 space-y-2.5 shadow-[3px_3px_0px_#000]" data-testid="ss-outro-panel">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-[#FFD028]" />
            <span className="font-mono text-[9px] font-black uppercase tracking-widest text-white">MISTECK Outro · system footer</span>
          </div>
          <p className="font-mono text-[8px] text-white/45 leading-relaxed">A branded 5s animated outro (JOIN MISTECK.COM) built locally & saved to your Main Vault.</p>
          <div className="flex gap-2">
            <button onClick={genOutro} disabled={outroBusy} data-testid="ss-outro-generate"
              className="flex-1 flex items-center justify-center gap-1.5 border-2 border-black bg-[#FFD028] text-black py-2.5 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
              {outroBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {outroId ? "Rebuild" : "Generate Outro"}
            </button>
            {outroId && (
              <button onClick={downloadOutro} data-testid="ss-outro-download"
                className="flex items-center justify-center gap-1.5 border-2 border-black bg-black text-[#FFD028] px-3 py-2.5 font-mono text-[9px] font-black uppercase tracking-widest active:bg-[#FFD028] active:text-black">
                <Download size={13} /> .mp4
              </button>
            )}
          </div>
          {outroId && (
            <label className="flex items-center gap-2 cursor-pointer" data-testid="ss-outro-use">
              <input type="checkbox" checked={useOutro} onChange={(e) => setUseOutro(e.target.checked)} className="accent-[#FFD028] w-4 h-4" />
              <span className="font-mono text-[9px] uppercase tracking-widest text-white/70">Append MISTECK outro to the end of this reel</span>
            </label>
          )}
        </div>

        {/* Compile */}
        <button onClick={compile} disabled={compiling || scenes.length === 0} data-testid="ss-compile"
          className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFD028] text-black py-3.5 font-mono text-[11px] font-black uppercase tracking-widest shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50">
          {compiling ? <Loader2 size={16} className="animate-spin" /> : <Scissors size={16} />} {compiling ? "Creating your reel…" : "Create My Reel (free)"}
        </button>
        {compiling && (
          <div className="flex justify-center pt-1"><RenderProgress label="Creating your reel" testid="ss-progress" /></div>
        )}

        {resultId && (
          <div className="border-2 border-[#00E5C0] bg-[#0d0d0d] p-3 space-y-2.5" data-testid="ss-result">
            <p className="font-mono text-[9px] font-black uppercase tracking-widest text-[#00E5C0] flex items-center gap-1.5"><CheckCircle2 size={13} /> Saved to Main Vault · ready in Mist Director</p>
            <div className="aspect-[9/16] max-h-72 mx-auto border-2 border-black bg-black overflow-hidden"><ClipThumb assetId={resultId} /></div>
            <button onClick={sendToDirector} data-testid="ss-send-to-director"
              className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFD028] text-black py-3.5 font-mono text-[11px] font-black uppercase tracking-widest shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none">
              <Send size={16} /> Send my reel to Mist Director →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default StitchingStudio;
